import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Crown, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PLANS, TIER_TABLE } from "@/lib/tiers";

export function PlanChooser({ testIdPrefix = "paywall" }) {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [cycle, setCycle] = useState("annual");
  const [starting, setStarting] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const plan = PLANS[cycle];
  const trialEligible = user ? !user.trial_used : true;

  const startCheckout = async () => {
    if (!user) { navigate("/auth"); return; }
    setStarting(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: plan.lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start checkout.");
      setStarting(false);
    }
  };

  const restore = async () => {
    if (!user) { navigate("/auth"); return; }
    setRestoring(true);
    try {
      await api.post("/billing/restore");
      await refreshUser();
      toast.success("Your subscription has been restored!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "No subscription found.");
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div>
      <div className="flex justify-center">
        <div className="inline-flex border border-[#D9D4CC] rounded-full p-1 bg-[#F3F0EA]">
          {["monthly", "annual"].map((c) => (
            <button
              key={c}
              data-testid={`${testIdPrefix}-cycle-${c}`}
              onClick={() => setCycle(c)}
              className={`relative px-5 py-2 text-sm rounded-full transition-colors duration-300 ${
                cycle === c ? "bg-[#2C2A29] text-[#FAF9F6]" : "text-[#4A4744] hover:text-[#2C2A29]"
              }`}
            >
              {c === "monthly" ? "Monthly" : "Annual"}
              {c === "annual" && (
                <span className="ml-1.5 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#9B7E4E] text-[#FAF9F6]">
                  Save 17%
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="text-center mt-6">
        <p className="font-heading text-4xl text-[#2C2A29]" data-testid={`${testIdPrefix}-price`}>
          {plan.price}
          <span className="font-body text-base text-[#7A756E] ml-1.5">{plan.cadence}</span>
        </p>
        {cycle === "annual" && (
          <p className="font-body text-sm text-[#9B7E4E] mt-1" data-testid={`${testIdPrefix}-annual-badge`}>
            2 months free vs. paying monthly
          </p>
        )}
      </div>

      <button
        data-testid={`${testIdPrefix}-subscribe-button`}
        onClick={startCheckout}
        disabled={starting}
        className="mt-6 w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#9B7E4E] text-[#FAF9F6] rounded-sm hover:opacity-90 transition-opacity duration-300 disabled:opacity-50"
      >
        {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
        {trialEligible ? "Try Premium free for 7 days" : "Upgrade to Premium"}
      </button>
      <p className="font-body text-xs text-[#7A756E] text-center mt-2.5">
        {trialEligible
          ? `7-day free trial, then ${plan.price} ${plan.cadence}. Auto-renews; cancel anytime.`
          : `${plan.price} ${plan.cadence}. Auto-renews; cancel anytime.`}
      </p>

      <div className="flex items-center justify-center gap-4 mt-4 font-body text-xs text-[#7A756E]">
        <button data-testid={`${testIdPrefix}-restore-button`} onClick={restore} disabled={restoring}
          className="underline underline-offset-2 hover:text-[#2C2A29] transition-colors duration-300 disabled:opacity-50">
          {restoring ? "Restoring…" : "Restore purchases"}
        </button>
        <a href="/terms" className="underline underline-offset-2 hover:text-[#2C2A29]">Terms</a>
        <a href="/privacy" className="underline underline-offset-2 hover:text-[#2C2A29]">Privacy</a>
      </div>
    </div>
  );
}

export function TierTable({ testIdPrefix = "paywall" }) {
  return (
    <div className="overflow-x-auto" data-testid={`${testIdPrefix}-tier-table`}>
      <table className="w-full text-left font-body text-sm">
        <thead>
          <tr className="border-b border-[#D9D4CC]">
            <th className="py-2.5 pr-3 font-normal text-[#7A756E]"> </th>
            <th className="py-2.5 px-3 font-normal text-[#7A756E]">Guest</th>
            <th className="py-2.5 px-3 font-normal text-[#7A756E]">Free</th>
            <th className="py-2.5 pl-3 font-semibold text-[#9B7E4E] flex items-center gap-1.5">
              <Crown className="w-3.5 h-3.5" /> Premium
            </th>
          </tr>
        </thead>
        <tbody>
          {TIER_TABLE.map((row) => (
            <tr key={row.feature} className="border-b border-[#EAE6DF] last:border-0">
              <td className="py-2.5 pr-3 text-[#2C2A29]">{row.feature}</td>
              <td className="py-2.5 px-3 text-[#7A756E]">{row.guest}</td>
              <td className="py-2.5 px-3 text-[#4A4744]">{row.free}</td>
              <td className="py-2.5 pl-3 text-[#2C2A29] font-medium">
                {row.premium === "Unlimited" || row.premium === "Included"
                  ? <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5 text-[#9B7E4E]" />{row.premium}</span>
                  : row.premium}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
